// Pure-JS MD5 (RFC 1321), operating on bytes and returning lowercase hex.
//
// MD5 isn't in the Web Crypto API (which only offers SHA-1/256/384/512), but
// it's still what a lot of legacy forensic tooling (EnCase, FTK, older chain-
// of-custody logs) records alongside SHA-1, so the Hash Verifier tool needs a
// real implementation rather than skipping it.
//
// NOT cryptographically secure — MD5 is broken for collision resistance.
// Offered here purely for interoperability with a hash value someone already
// recorded elsewhere, never as a security recommendation.

const S = [
  7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22,
  5, 9, 14, 20, 5, 9, 14, 20, 5, 9, 14, 20, 5, 9, 14, 20,
  4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23,
  6, 10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21,
];

// K[i] = floor(abs(sin(i+1)) * 2^32), per RFC 1321 §3.4 — computed rather than
// hand-transcribed, so there's no chance of a typo in 64 magic constants.
const K = new Uint32Array(64);
for (let i = 0; i < 64; i++) {
  K[i] = Math.floor(Math.abs(Math.sin(i + 1)) * 2 ** 32);
}

function rotl(x, n) {
  return (x << n) | (x >>> (32 - n));
}

/** Pad per RFC 1321 §3.1 and return the message as 32-bit little-endian words. */
function padMessage(bytes) {
  const bitLen = bytes.length * 8;

  // original bytes + one 0x80 byte + zeros, padded so length ≡ 56 (mod 64),
  // then 8 more bytes for the 64-bit original bit-length.
  let paddedLen = bytes.length + 1;
  while (paddedLen % 64 !== 56) paddedLen++;
  paddedLen += 8;

  const padded = new Uint8Array(paddedLen);
  padded.set(bytes);
  padded[bytes.length] = 0x80;

  const dv = new DataView(padded.buffer);
  // Little-endian 64-bit bit-length. File sizes here never approach 2^53
  // bytes, so the high word is always 0 in practice — written anyway for
  // correctness against the spec.
  dv.setUint32(paddedLen - 8, bitLen >>> 0, true);
  dv.setUint32(paddedLen - 4, Math.floor(bitLen / 0x100000000), true);

  const words = new Uint32Array(paddedLen / 4);
  for (let i = 0; i < words.length; i++) words[i] = dv.getUint32(i * 4, true);
  return words;
}

/**
 * @param {Uint8Array} bytes
 * @returns {string} 32-character lowercase hex digest
 */
export function md5(bytes) {
  const words = padMessage(bytes);

  let a0 = 0x67452301;
  let b0 = 0xefcdab89;
  let c0 = 0x98badcfe;
  let d0 = 0x10325476;

  for (let chunk = 0; chunk < words.length; chunk += 16) {
    let A = a0, B = b0, C = c0, D = d0;

    for (let i = 0; i < 64; i++) {
      let F, g;
      if (i < 16) {
        F = (B & C) | (~B & D);
        g = i;
      } else if (i < 32) {
        F = (D & B) | (~D & C);
        g = (5 * i + 1) % 16;
      } else if (i < 48) {
        F = B ^ C ^ D;
        g = (3 * i + 5) % 16;
      } else {
        F = C ^ (B | ~D);
        g = (7 * i) % 16;
      }

      // Bitwise ops (& | ^ ~) act on the 32-bit pattern regardless of whether
      // JS is currently showing that pattern as a signed or unsigned decimal
      // value; plain `+` addition of those (possibly "negative") values still
      // sums to the right total modulo 2^32, which >>> 0 then normalizes back
      // into an unsigned 32-bit result. Standard technique for MD5-in-JS.
      F = (F + A + K[i] + words[chunk + g]) >>> 0;
      A = D;
      D = C;
      C = B;
      B = (B + rotl(F, S[i])) >>> 0;
    }

    a0 = (a0 + A) >>> 0;
    b0 = (b0 + B) >>> 0;
    c0 = (c0 + C) >>> 0;
    d0 = (d0 + D) >>> 0;
  }

  const hexLE = (n) => {
    let hex = '';
    for (let i = 0; i < 4; i++) hex += ((n >>> (i * 8)) & 0xff).toString(16).padStart(2, '0');
    return hex;
  };

  return hexLE(a0) + hexLE(b0) + hexLE(c0) + hexLE(d0);
}
